import { makeStyles } from '@mui/styles';
import { CircularProgress, Grid, Loader, Typography } from 'apollo-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';

import {
  fetchChangedItemsList,
  fetchWorkflowsList,
  validateOnPreview,
} from './actions/export.actions';
import ConfirmCancelDialog from './components/common/ConfirmCancelDialog';
import ExportFooter from './components/export/ExportFooter';
import ExportInfo from './components/export/ExportInfo';
import ResultsTable from './components/export/ResultsTable';
import StartExport from './components/export/StartExport';
import StartExportPackages from './components/export/StartExportPackages';
import {
  cancelModalText,
  EXPORT_POLLING_TIME,
  EXPORT_STEPS,
  INCLUDED_ACTORS_TABLE_KEY,
  PACKAGES_EXPORT,
  STEPS_DESCRIPTION,
} from './constants/export.constants';
import { getChangedItems } from './selectors/export.selectors';
import {
  getExportDataInitState,
  getExportPayload,
  getExportPreviewPackagesPayload,
  getExportPreviewPayload,
  getModifiedActorsRows,
  getSaveBuildFilePayload,
  modifyDate,
  modifyRows,
  updateExportStatuses,
  updateStatusesWithCancelResponse,
} from './utils/export.utils';
import { clearErrors, handleErrors } from '../actions/ErrorActions';
import { clearWarnings } from '../actions/WarningActions';
import {
  appMigrationCancel,
  appMigrationExport,
  appMigrationExportDownload,
  appMigrationExportPreview,
  appMigrationImportStatus,
  appMigrationSaveBuildFile,
} from '../api/app-migration';
import { StepProgress } from '../components/StepProgress';
import * as Urls from '../constants/Urls';
import { useTranslation } from '../custom-hooks/useTranslation';
import { selectUserName } from '../selectors/AuthSelectors';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    overflow: 'hidden',
  },
  main: {
    padding: '20px 40px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',

    backgroundColor: '#fff',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
  footer: {
    height: 64,
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    fontStretch: 'normal',
    fontStyle: 'normal',
    lineHeight: 1,
    letterSpacing: 'normal',
    color: '#000',
    marginLeft: 17,
    padding: '24px 20px 18px 5px',
  },
  loaderContainer: {
    height: 'calc(100vh - 120px)',
    width: '95%',
    position: 'fixed',
    zIndex: 100,
    marginLeft: 10,
  },
  circularProgress: {
    marginBottom: 20,
  },
});

const AppMigrationExport = ({ type }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const translateString = useTranslation();

  const changedItemsList = useSelector(getChangedItems);

  const userName = useSelector(selectUserName);

  const isPackagesExport = type === PACKAGES_EXPORT;

  const defaultPackageFileName = isPackagesExport ? '' : `${userName} - ${modifyDate(new Date())}`;

  const [exportStep, setExportStep] = useState(EXPORT_STEPS.INITIAL_STEP);
  const [isLoading, setIsLoading] = useState(false);
  const [isExportProcessing, setIsExportProcessing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isConfirmCancelDialogOpen, setIsConfirmCancelDialogOpen] = useState(false);

  const [exportData, setExportData] = useState(getExportDataInitState({ changedItemsList }));
  const [resultRows, setResultRows] = useState([]);
  const [selectedResultRows, setSelectedResultRows] = useState(new Set());
  const [processedItemsNumber, setProcessedItemsNumber] = useState(0);
  const processedItemsNumberRef = useRef(0);
  const cancelRef = useRef();

  const [isCancelProcess, setIsCancelProcess] = useState(false);

  const [targetDirectoryName, setTargetDirectoryName] = useState();

  const [isSavedBuildFile, setIsSavedBuildFile] = useState(false);
  const [savedBuildFileRows, setSavedBuildFileRows] = useState(new Set());
  const [buildFileComment, setBuildFileComment] = useState('');
  const [buildFileName, setBuildFileName] = useState('' || defaultPackageFileName);
  const [buildFileUser, setBuildFileUser] = useState('');
  const [selectedPackages, setSelectedPackages] = useState([]);

  const progress = useMemo(() => Math.round((processedItemsNumber * 100) / resultRows.length), [
    processedItemsNumber,
    resultRows.length,
  ]);

  const pageTitle = useMemo(
    () =>
      isPackagesExport
        ? 'Export Application Components - by Package File'
        : 'Export Application Components',
    [isPackagesExport]
  );

  const updateExportData = useCallback((key, value) => {
    setExportData((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  }, []);

  useEffect(() => {
    document.title = translateString(pageTitle);

    if (!isPackagesExport) {
      dispatch(fetchWorkflowsList());
      dispatch(fetchChangedItemsList());
    }

    return () => {
      dispatch(clearWarnings());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isPackagesExport) {
      updateExportData('selectedChangedItems', changedItemsList?.map((item) => item.Value) || []);
    }
  }, [changedItemsList, isPackagesExport, updateExportData]);

  useEffect(() => {
    setExportStep(EXPORT_STEPS.INITIAL_STEP);

    isPackagesExport && setBuildFileName('');
  }, [isPackagesExport, type]);

  const handleError = (dispatch, error) => {
    dispatch(handleErrors(error, { isTopBanner: true }));
    setHasError(true);
  };

  const handlePreview = useCallback(async () => {
    const validationErrors = dispatch(
      validateOnPreview({ exportData, selectedPackages, isPackagesExport })
    );

    dispatch(clearErrors());

    if (validationErrors?.length) {
      return;
    }

    const payload = isPackagesExport
      ? getExportPreviewPackagesPayload({ selectedPackages })
      : getExportPreviewPayload({ exportData });

    setIsLoading(true);

    try {
      const response = await appMigrationExportPreview(payload);

      if (response.hasError) {
        dispatch(handleErrors(response.errors, { isTopBanner: true }));
        setIsLoading(false);
        return;
      }

      const rows = modifyRows(response.previewComponents);
      const selectedRows = new Set(rows.map((row) => row.id));

      setExportStep(EXPORT_STEPS.START_EXPORT);
      setResultRows(rows);
      setSelectedResultRows(selectedRows);
      setTargetDirectoryName(response.targetDirectoryName);
    } catch (error) {
      handleError(dispatch, error);
    }

    setIsLoading(false);
  }, [dispatch, exportData, isPackagesExport, selectedPackages]);

  const getItemStatuses = useCallback(async () => {
    if (cancelRef.current || processedItemsNumberRef.current >= resultRows.length) {
      return;
    }

    const response = await appMigrationImportStatus({ importKey: targetDirectoryName });
    const { workItemStatuses = [] } = response;

    if (!workItemStatuses.length || workItemStatuses.length >= resultRows.length) {
      return;
    }

    setResultRows((rows) => updateExportStatuses(workItemStatuses, rows));

    processedItemsNumberRef.current = workItemStatuses.length;
    setProcessedItemsNumber(processedItemsNumberRef.current);

    setTimeout(getItemStatuses, EXPORT_POLLING_TIME);
  }, [targetDirectoryName, resultRows.length]);

  const handleExport = useCallback(
    async ({ resultRows, selectedResultRows }) => {
      const payload = getExportPayload({
        rows: resultRows,
        selectedRows: selectedResultRows,
        selectedDate: exportData.selectedDate,
        targetDirectoryName,
        buildFileName,
        buildFileComment,
        buildFileUser,
      });

      setExportStep(EXPORT_STEPS.PREVIEW);
      setIsExportProcessing(true);

      setTimeout(getItemStatuses, EXPORT_POLLING_TIME);

      try {
        const response = await appMigrationExport(payload);

        if (response.hasError) {
          handleError(dispatch, response.errors);
          setIsExportProcessing(false);
          return;
        }

        setExportStep(EXPORT_STEPS.EXPORT_COMPLETED);

        if (!cancelRef?.current) {
          setResultRows(response.exportedComponents);
        }
      } catch (error) {
        handleError(dispatch, error);
      }

      setIsExportProcessing(false);
    },
    [
      exportData.selectedDate,
      targetDirectoryName,
      buildFileName,
      buildFileComment,
      buildFileUser,
      getItemStatuses,
      dispatch,
    ]
  );

  const handleDownloadFile = useCallback(
    async (targetDirectoryName) => {
      try {
        await appMigrationExportDownload({ targetDirectoryName });
      } catch (error) {
        dispatch(handleErrors(error, { isTopBanner: true }));
      }
    },
    [dispatch]
  );

  const handleStartNewExport = useCallback(() => {
    setIsCancelProcess(false);
    setExportData(getExportDataInitState({ changedItemsList }));
    setResultRows([]);
    setSelectedResultRows(new Set([]));
    setTargetDirectoryName();
    setExportStep(EXPORT_STEPS.INITIAL_STEP);
    setProcessedItemsNumber(0);
    setSelectedPackages([]);

    setIsSavedBuildFile(false);
    setBuildFileName(isPackagesExport ? '' : defaultPackageFileName);
    setBuildFileComment('');
    setBuildFileUser('');

    processedItemsNumberRef.current = 0;
    cancelRef.current = false;
  }, [changedItemsList, defaultPackageFileName, isPackagesExport]);

  const handleBackButton = () => {
    setResultRows([]);
    setSelectedResultRows(new Set());
    setExportStep(EXPORT_STEPS.INITIAL_STEP);

    setIsSavedBuildFile(false);
    setBuildFileName(isPackagesExport ? '' : defaultPackageFileName);
    setBuildFileComment('');
    setBuildFileUser('');
  };

  const handleCancel = useCallback(() => {
    if (exportStep === EXPORT_STEPS.PREVIEW) {
      setIsConfirmCancelDialogOpen(true);

      return;
    }
    history.push(Urls.APP_MIGRATION_PAGE);
  }, [history, exportStep]);

  const handleAddActor = useCallback(
    ({ actorsTableKey, newRow }) => {
      updateExportData(actorsTableKey, [newRow, ...exportData[actorsTableKey]]);
    },
    [exportData, updateExportData]
  );

  const handleSelectActor = useCallback(
    ({ rowId, actorsTableKey, data = {} }) => {
      setExportData((prevExportData) => {
        const updatedRows = getModifiedActorsRows({
          rowId,
          previousRows: prevExportData[actorsTableKey],
          newRows: actorsTableKey === INCLUDED_ACTORS_TABLE_KEY ? [data] : data,
        });

        const updatedExportData = { ...prevExportData, [actorsTableKey]: updatedRows };

        dispatch(
          validateOnPreview({ exportData: updatedExportData, selectedPackages, isPackagesExport })
        );

        return updatedExportData;
      });
    },
    [dispatch, isPackagesExport, selectedPackages]
  );

  const handleDeleteActor = useCallback(
    ({ rowId, actorsTableKey }) => {
      setExportData((prevExportData) => {
        const updatedActors = exportData[actorsTableKey].filter((actor) => actor.rowId !== rowId);

        const updatedExportData = { ...prevExportData, [actorsTableKey]: updatedActors };

        dispatch(
          validateOnPreview({ exportData: updatedExportData, selectedPackages, isPackagesExport })
        );

        return updatedExportData;
      });
    },
    [exportData, dispatch, selectedPackages, isPackagesExport]
  );

  const handleConfirmCancel = useCallback(async () => {
    setIsLoading(true);
    setIsConfirmCancelDialogOpen(false);
    cancelRef.current = true;
    setIsCancelProcess(true);

    try {
      const response = await appMigrationCancel({
        importKey: targetDirectoryName,
        isCancelExportProcess: true,
      });

      if (response.hasError) {
        handleError(dispatch, response.errors);
        setIsLoading(false);
        return;
      }

      const updatedResultRows = updateStatusesWithCancelResponse(
        response.workItemStatuses,
        resultRows
      );
      setResultRows(updatedResultRows);
    } catch (error) {
      handleError(dispatch, error);
    }

    setIsSavedBuildFile(false);
    setBuildFileName(isPackagesExport ? '' : defaultPackageFileName);
    setBuildFileComment('');
    setBuildFileUser('');

    setIsLoading(false);
  }, [defaultPackageFileName, dispatch, isPackagesExport, resultRows, targetDirectoryName]);

  const handleCheckIn = useCallback(
    async ({ resultRows, selectedResultRows }) => {
      const payload = getSaveBuildFilePayload({
        rows: resultRows,
        selectedRows: selectedResultRows,
        buildFileComment,
        buildFileName,
        buildFileUser,
        selectedDate: exportData.selectedDate,
      });

      setIsLoading(true);

      try {
        const response = await appMigrationSaveBuildFile(payload);
        if (response?.hasError) {
          handleError(dispatch, response.errors);
          setIsLoading(false);
          return;
        } else if (response?.saveBuildFileStatus) {
          setSavedBuildFileRows(selectedResultRows);
          setIsSavedBuildFile(true);

          setBuildFileName(defaultPackageFileName);
          setBuildFileComment('');
          setBuildFileUser('');
        }

        if (cancelRef?.current) {
          setIsSavedBuildFile(false);
        }
      } catch (error) {
        handleError(dispatch, error);
      }

      setIsLoading(false);
    },
    [
      buildFileComment,
      buildFileName,
      buildFileUser,
      defaultPackageFileName,
      dispatch,
      exportData.selectedDate,
    ]
  );

  const handleUpdatePackages = useCallback(
    ({ packages }) => {
      if (packages.length > 0) {
        dispatch(validateOnPreview({ exportData, selectedPackages: packages, isPackagesExport }));
      }
      setSelectedPackages(packages);
    },
    [dispatch, exportData, isPackagesExport]
  );

  const showStartExport = exportStep === EXPORT_STEPS.INITIAL_STEP;
  const showResultTable = exportStep > EXPORT_STEPS.INITIAL_STEP;

  return (
    <>
      {isLoading && (
        <div className={classes.loaderContainer}>
          <Loader isInner />
        </div>
      )}
      <Grid container className={classes.container}>
        <Grid item xs={12}>
          <Typography className={classes.title}>{translateString(pageTitle)}</Typography>
        </Grid>
        <Grid item xs={12} className={classes.main}>
          <StepProgress activeStep={exportStep} steps={STEPS_DESCRIPTION} hasError={hasError} />
          {isExportProcessing && (
            <CircularProgress
              className={classes.circularProgress}
              value={progress}
              thickness={3}
              statusText={`${translateString(
                'Processing'
              )} ${processedItemsNumber} ${translateString('out of')} ${resultRows.length}`}
            />
          )}
          <div className={classes.contentWrapper} style={{ width: '100%' }}>
            <ExportInfo
              exportStep={exportStep}
              resultRows={resultRows}
              isPackagesExport={isPackagesExport}
            />
            {showStartExport && (
              <Grid item xs={12}>
                {isPackagesExport ? (
                  <StartExportPackages
                    handleUpdatePackages={handleUpdatePackages}
                    selectedPackages={selectedPackages}
                  />
                ) : (
                  <StartExport
                    exportData={exportData}
                    changedItemsList={changedItemsList}
                    updateExportData={updateExportData}
                    handleAddActor={handleAddActor}
                    handleSelectActor={handleSelectActor}
                    handleDeleteActor={handleDeleteActor}
                  />
                )}
              </Grid>
            )}
            {showResultTable && (
              <Grid item xs={12} style={{ width: '100%', marginBottom: 10 }}>
                <ResultsTable
                  rows={resultRows}
                  selectedRows={selectedResultRows}
                  setSelectedRows={setSelectedResultRows}
                  setResultRows={setResultRows}
                  isPreviewStep={exportStep === EXPORT_STEPS.START_EXPORT}
                  buildFileComment={buildFileComment}
                  setBuildFileComment={setBuildFileComment}
                  buildFileName={buildFileName}
                  setBuildFileName={setBuildFileName}
                  setBuildFileUser={setBuildFileUser}
                  buildFileUser={buildFileUser}
                  isSavedBuildFile={isSavedBuildFile}
                  savedBuildFileRows={savedBuildFileRows}
                  isPackagesExport={isPackagesExport}
                />
              </Grid>
            )}
          </div>
        </Grid>
      </Grid>
      <Grid container xs={12} className={classes.footer}>
        <ExportFooter
          isCancelExportProcess={isCancelProcess}
          isLoading={isLoading}
          isSavedBuildFile={isSavedBuildFile}
          onCheckIn={() => handleCheckIn({ resultRows, selectedResultRows })}
          exportStep={exportStep}
          onCancel={handleCancel}
          onPreview={handlePreview}
          onExport={() => handleExport({ resultRows, selectedResultRows })}
          onDownload={() => handleDownloadFile(targetDirectoryName)}
          onStartNew={handleStartNewExport}
          handleBackButton={handleBackButton}
          selectedResultRows={selectedResultRows}
          isPackagesExport={isPackagesExport}
        />
      </Grid>
      {isConfirmCancelDialogOpen && (
        <ConfirmCancelDialog
          handleConfirmCancel={handleConfirmCancel}
          onClose={() => setIsConfirmCancelDialogOpen(false)}
          open={isConfirmCancelDialogOpen}
          text={cancelModalText}
        />
      )}
    </>
  );
};

export default AppMigrationExport;
